/**
 * UnifiedHealthTwinCard — iter603 Command Center widget.
 *
 * Shows 3-twin snapshot (Nutritional + Cognitive + Lab) + Claude cross-twin
 * insight + single "Какво да правя днес" CTA. Embedded in /dashboard.
 */
import React, { useEffect, useState } from "react";
import axios from "axios";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/App";
import { useTheme } from "@/theme/ThemeContext";
import { Button } from "@/components/ui/button";
import { Brain, FlaskConical, Apple, Sparkles, Loader2, ArrowRight, TrendingUp, TrendingDown, Minus } from "lucide-react";
import LunchNudgeBanner from "@/components/LunchNudgeBanner";
import CrossTwinInsightsCard from "@/components/CrossTwinInsightsCard";
import { TWIN_TOOLTIPS } from "@/lib/twinTooltips";
import { getMood, summariseMoods } from "@/lib/twinMood";
import TwinCascadeToast from "@/components/dashboard/TwinCascadeToast";
import TwinLeverageCard from "@/components/dashboard/TwinLeverageCard";
import { TWIN_CATALOG, twinShort, twinLabel } from "@/lib/twinCatalog";

// iter631 — helper to render native tooltip text for a twin tile
const twinTitle = (key) => {
  const t = TWIN_TOOLTIPS[key];
  if (!t) return "";
  return `${t.title}\n\n${t.description}\n\nИзмерва: ${t.measures}`;
};

import {
  ResponsiveContainer,
  LineChart,
  Line,
  XAxis,
  YAxis,
  Tooltip as RTooltip,
  ReferenceLine,
} from "recharts";

export default function UnifiedHealthTwinCard() {
  // iter687/iter689 — gender-aware UI (BHI tile copy adjusts for male users)
  const { API, user } = useAuth();
  const { isDark } = useTheme();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [trends, setTrends] = useState(null);
  const [trendDays, setTrendDays] = useState(30);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    axios
      .get(`${API}/health-twin/insights`, { timeout: 35000 })
      .then(({ data: d }) => setData(d))
      .catch(() => {})
      .finally(() => setLoading(false));
  }, [API]);

  // iter605 — Phase C: trends (re-fetch when window changes)
  useEffect(() => {
    axios
      .get(`${API}/health-twin/trends?days=${trendDays}`, { timeout: 15000 })
      .then(({ data: t }) => setTrends(t))
      .catch(() => {});
  }, [API, trendDays]);

  // iter610 — Twin Score (composite 0-100 from all available twins)
  // iter627 — refetch every 5 min + on meal log events so real-time fasting penalty stays current
  const [twinScore, setTwinScore] = useState(null);
  useEffect(() => {
    const load = () => {
      axios
        .get(`${API}/health-twin/twin-score?days=30`, { timeout: 15000 })
        .then(({ data: ts }) => setTwinScore(ts))
        .catch(() => {});
    };
    load();
    const onMealChange = () => load();
    const twinEvts = [
      "meal-logged", "meal-added", "meal-log-changed", "nutramilo:meals:updated",
      "cortisol-meal-changed", "twin-score-changed",
      "cardio-log-changed", "metabolic-log-changed", "movement-log-changed",
      "mental-log-changed", "liver-kidney-log-changed",
      "thyroid-lab-changed", "stress-log-changed", "cycle-changed",
    ];
    twinEvts.forEach(e => window.addEventListener(e, onMealChange));
    // iter633 — refetch when user returns to tab
    const onVis = () => { if (document.visibilityState === "visible") load(); };
    document.addEventListener("visibilitychange", onVis);
    const interval = setInterval(load, 5 * 60 * 1000);
    return () => {
      twinEvts.forEach(e => window.removeEventListener(e, onMealChange));
      document.removeEventListener("visibilitychange", onVis);
      clearInterval(interval);
    };
  }, [API]);

  const cardCls = isDark
    ? "bg-gradient-to-br from-slate-900/60 to-indigo-900/30 border-indigo-500/20"
    : "bg-gradient-to-br from-white to-indigo-50/40 border-indigo-200";

  const [phenotype, setPhenotype] = useState(null);  // iter609
  const [microbiome, setMicrobiome] = useState(null);  // iter609
  const [bodyComp, setBodyComp] = useState(null);  // iter612
  const [sleep, setSleep] = useState(null);  // iter612
  const [stress, setStress] = useState(null);  // iter618
  const [thyroid, setThyroid] = useState(null);  // iter621
  const [bhi, setBhi] = useState(null);  // iter675 — Breast Health Intelligence (18-ти близнак)

  useEffect(() => {
    axios.get(`${API}/phenotype-twin/me`, { timeout: 10000 })
      .then(({ data }) => setPhenotype(data.current))
      .catch(() => {});
    axios.get(`${API}/microbiome-twin/snapshot`, { timeout: 10000 })
      .then(({ data }) => setMicrobiome(data))
      .catch(() => {});
    axios.get(`${API}/body-composition-twin/snapshot`, { timeout: 10000 })
      .then(({ data }) => setBodyComp(data.current))
      .catch(() => {});
    axios.get(`${API}/sleep-twin/snapshot`, { timeout: 10000 })
      .then(({ data }) => setSleep(data))
      .catch(() => {});
    axios.get(`${API}/stress-twin/snapshot`, { timeout: 10000 })
      .then(({ data }) => setStress(data))
      .catch(() => {});
    axios.get(`${API}/thyroid-twin/snapshot`, { timeout: 10000 })
      .then(({ data }) => setThyroid(data))
      .catch(() => {});
    // iter675 — fetch BHI snapshot for 18-ти tile (gracefully ignores if unavailable / male user)
    axios.get(`${API}/breast-intelligence/snapshot`, { timeout: 12000 })
      .then(({ data }) => setBhi(data))
      .catch(() => {});
    // iter623 — Refetch thyroid + stress when lab is updated elsewhere
    const handler = () => {
      axios.get(`${API}/thyroid-twin/snapshot`, { timeout: 10000 })
        .then(({ data }) => setThyroid(data))
        .catch(() => {});
      axios.get(`${API}/stress-twin/snapshot`, { timeout: 10000 })
        .then(({ data }) => setStress(data))
        .catch(() => {});
    };
    window.addEventListener("thyroid-lab-changed", handler);
    window.addEventListener("stress-log-changed", handler);
    return () => {
      window.removeEventListener("thyroid-lab-changed", handler);
      window.removeEventListener("stress-log-changed", handler);
    };
  }, [API]);

  const snap = data?.snapshot;
  const nutri = snap?.nutritional || {};
  const cog = snap?.cognitive || {};
  const lab = snap?.lab || {};
  const labValues = lab.values || {};
  const labCount = Object.keys(labValues).length;

  // iter675 — Composite BHI score (average of metabolic components scores; lower is better
  // because each component represents "risk pressure" 0-100). We invert so the tile shows
  // a "health-style" 0-100 number consistent with the other twins.
  const bhiComposite = (() => {
    const comp = bhi?.metabolic?.components;
    if (!comp) return null;
    const scores = ["estrogen", "insulin", "igf1", "inflammation", "adiposity"]
      .map((k) => comp[k]?.score)
      .filter((v) => typeof v === "number");
    if (!scores.length) return null;
    return Math.round(scores.reduce((a, b) => a + b, 0) / scores.length);
  })();
  const bhiDensityPct = bhi?.density?.density_percent ?? null;

  const dilColor = (v) =>
    v == null ? "text-slate-400"
    : v < 30 ? "text-emerald-500"
    : v < 50 ? "text-lime-500"
    : v < 70 ? "text-amber-500"
    : "text-rose-500";
  const cogColor = (v) =>
    v == null ? "text-slate-400"
    : v >= 70 ? "text-emerald-500"
    : v >= 50 ? "text-amber-500"
    : "text-rose-500";

  // iter1045 — плочката отваря мрежата, филтрирана по връзките на този близнак
  const openTwinNetwork = (id) => navigate(`/twins/network?twin=${id}`);

  // iter1045 — кои близнаци още нямат данни + едно действие за всеки
  const missingTwins = (() => {
    if (!twinScore?.twins_all) return [];
    const subs = { ...(twinScore.sub_scores || {}) };
    if (subs.bhi == null && bhiComposite != null) subs.bhi = bhiComposite;
    return twinScore.twins_all
      .filter((id) => subs[id] == null && TWIN_CATALOG[id])
      .map((id) => ({ id, ...TWIN_CATALOG[id] }));
  })();

  return (
    <div id="unified-health-twin" className={`p-4 rounded-2xl border shadow-sm scroll-mt-20 ${cardCls}`} data-testid="unified-health-twin-card">
      <div className="flex items-center gap-2 mb-3">
        <Sparkles className="w-4 h-4 text-indigo-500" />
        <h2 className="font-bold text-sm">🧬 Обединен здравен близнак</h2>
        <span
          className={`text-[10px] ${isDark ? "text-slate-500" : "text-slate-400"}`}
          data-testid="unified-twin-count"
          title="Броят се смята от реалните данни. Всички близнаци от живата физиологична мрежа са тук."
        >
          {twinScore?.twins_total
            ? `${twinScore.twins_with_data} от ${twinScore.twins_total} близнака с данни`
            : "близнаците се изчисляват…"}
        </span>
        <button
          onClick={() => navigate("/twins/network")}
          data-testid="twin-network-link"
          className={`ml-auto text-[10px] font-semibold flex items-center gap-1 px-2 py-0.5 rounded-full transition-colors ${
            isDark
              ? "bg-indigo-500/15 text-indigo-300 hover:bg-indigo-500/25"
              : "bg-indigo-50 text-indigo-700 hover:bg-indigo-100"
          }`}
          title="Виж живата физиологична мрежа между близнаците"
        >
          🕸️ Виж мрежата →
        </button>
      </div>

      {/* iter628 — Proactive lunch nudge (14:00 ч. browser notification + in-app banner) */}
      <LunchNudgeBanner />

      {/* iter627 — Real-time fasting awareness banner (above Twin Score) */}
      {twinScore?.realtime_state?.message_bg && (
        <div
          className={`p-3 rounded-xl mb-3 border-2 ${
            (twinScore.realtime_state.penalty_pts || 0) >= 25
              ? (isDark ? "bg-rose-900/30 border-rose-500/50" : "bg-rose-50 border-rose-300")
              : (isDark ? "bg-amber-900/30 border-amber-500/50" : "bg-amber-50 border-amber-300")
          }`}
          data-testid="realtime-fasting-banner"
        >
          <div className="flex items-start gap-3">
            <div className="text-xl mt-0.5">
              {(twinScore.realtime_state.penalty_pts || 0) >= 25 ? "🚨" : "⚠️"}
            </div>
            <div className="flex-1 min-w-0">
              <div className={`text-[11px] font-bold uppercase tracking-wider ${
                (twinScore.realtime_state.penalty_pts || 0) >= 25
                  ? (isDark ? "text-rose-300" : "text-rose-700")
                  : (isDark ? "text-amber-300" : "text-amber-700")
              }`}>
                Сигнал в момента · {twinScore.realtime_state.local_time}
              </div>
              <p className="text-xs sm:text-sm mt-0.5 leading-relaxed">
                {twinScore.realtime_state.message_bg}
              </p>
              {twinScore.realtime_state.cognitive_base != null && twinScore.realtime_state.cognitive_base !== twinScore.realtime_state.cognitive_adjusted && (
                <div className="text-[11px] mt-1.5 opacity-80">
                  Мислене и концентрация: <span className="line-through opacity-60">{twinScore.realtime_state.cognitive_base}</span>
                  {" → "}
                  <span className="font-bold">{twinScore.realtime_state.cognitive_adjusted}</span>
                  <span className="opacity-60"> (−{twinScore.realtime_state.penalty_pts} т. заради дълго гладуване)</span>
                </div>
              )}
            </div>
            <button
              onClick={() => navigate("/dashboard/plate-simulator")}
              className={`shrink-0 px-3 py-1.5 rounded-lg text-xs font-bold transition-colors ${
                (twinScore.realtime_state.penalty_pts || 0) >= 25
                  ? "bg-rose-500 hover:bg-rose-600 text-white"
                  : "bg-amber-500 hover:bg-amber-600 text-white"
              }`}
              data-testid="realtime-log-meal-btn"
            >
              Запишете хапка
            </button>
          </div>
        </div>
      )}

      {/* iter610 — Twin Score composite banner (above tile grid) */}
      {twinScore && (
        <div className={`p-3 rounded-xl mb-3 ${isDark ? "bg-gradient-to-r from-indigo-900/40 to-purple-900/30 border border-indigo-500/20" : "bg-gradient-to-r from-indigo-50 to-purple-50 border border-indigo-200"}`} data-testid="twin-score-banner">
          <div className="flex items-center justify-between gap-3">
            <div>
              <div className={`text-[10px] uppercase font-bold ${isDark ? "text-indigo-300" : "text-indigo-700"}`}>
                🧬 Обща оценка на близнаците
              </div>
              <div className="flex items-baseline gap-2 mt-0.5">
                <span className={`text-4xl font-bold ${
                  twinScore.composite >= 75 ? "text-emerald-500"
                  : twinScore.composite >= 50 ? "text-amber-500"
                  : "text-rose-500"
                }`} data-testid="twin-score-value">{twinScore.composite}</span>
                <span className="text-xs opacity-50">/100</span>
                <span className="text-[10px] opacity-60 ml-2">
                  {twinScore.coverage_pct}% покритие
                </span>
              </div>
              <div className="text-[10px] opacity-70 mt-1 max-w-md">
                {twinScore.interpretation_bg}
              </div>
              {/* iter1063 — точка 5: съзнателното преливане на оценката за
                  гърдата в три близнака става видимо, а не скрито в сметките. */}
              {twinScore.cross_influence?.bhi_blended_into?.length > 0 && (
                <div
                  className={`text-[10px] mt-1.5 px-2 py-1 rounded-lg inline-block ${isDark ? "bg-rose-900/30 text-rose-200" : "bg-rose-50 text-rose-700"}`}
                  title={`Оценката „${twinLabel("bhi")}“ (${twinScore.cross_influence.bhi_blended_into[0].bhi_score}/100) участва с 15% в: ${twinScore.cross_influence.bhi_blended_into.map((b) => twinLabel(b.twin)).join(", ")}. Останалите 85% са от собствените данни на всеки близнак.`}
                  data-testid="bhi-blend-note"
                >
                  В {twinScore.cross_influence.bhi_blended_into.map((b) => twinLabel(b.twin)).join(", ")} влиза 15% от оценката „{twinLabel("bhi")}“ ({twinScore.cross_influence.bhi_blended_into[0].bhi_score}/100)
                </div>
              )}
            </div>
            {/* Mini sparkline */}
            {twinScore.history && twinScore.history.filter(p => p.score !== null).length >= 2 && (
              <div className="w-24 h-12 flex-shrink-0" data-testid="twin-score-spark">
                <ResponsiveContainer width="100%" height="100%">
                  <LineChart data={twinScore.history.filter(p => p.score !== null)} margin={{ top: 2, right: 2, bottom: 0, left: 2 }}>
                    <Line type="monotone" dataKey="score" stroke={isDark ? "#a5b4fc" : "#6366f1"} strokeWidth={2} dot={false} isAnimationActive={false} />
                  </LineChart>
                </ResponsiveContainer>
              </div>
            )}
          </div>
          {/* Breakdown bar */}
          <div className="flex gap-1 mt-2.5 flex-wrap">
            {[
              { id: "nutritional", label: "Х", color: "#10b981" },
              { id: "lab", label: "Л", color: "#0ea5e9" },
              { id: "phenotype", label: "Ф", color: "#a855f7" },
              { id: "microbiome", label: "М", color: "#22c55e" },
              { id: "body_comp", label: "Т", color: "#f59e0b" },
              { id: "sleep", label: "С", color: "#818cf8" },
              { id: "stress", label: "Сt", color: "#f97316" },
              { id: "cardio", label: "Кр", color: "#ef4444" },
              { id: "hormonal", label: "Хр", color: "#ec4899" },
              { id: "metabolic_insulin", label: "Ин", color: "#84cc16" },
              { id: "inflammation", label: "Вз", color: "#f59e0b" },
              { id: "movement", label: "Дв", color: "#0891b2" },
              { id: "liver_kidney", label: "Чд", color: "#3b82f6" },
              { id: "kidney", label: "Бб", color: "#14b8a6" },
              { id: "dental", label: "Зб", color: "#06b6d4" },
              { id: "bhi", label: "БГ", color: "#ec4899" },
              { id: "hematology", label: "Кв", color: "#dc2626" },
              { id: "respiratory", label: "Дш", color: "#0ea5e9" },
              { id: "neuro", label: "Нр", color: "#7c3aed" },
              { id: "male_health", label: "Мж", color: "#2563eb" },
            ].filter((t) => t.id !== "male_health" || twinScore.sub_scores?.male_health !== undefined).map((t) => {
              const val = t.id === "bhi"
                ? (twinScore.sub_scores?.bhi ?? bhiComposite ?? null)
                : (twinScore.sub_scores?.[t.id] ?? null);
              return (
                <div key={t.id} className="flex-1 text-center" data-testid={`twin-score-sub-${t.id}`}>
                  <div className={`h-1 rounded-full ${isDark ? "bg-slate-800" : "bg-slate-200"} overflow-hidden`}>
                    {val !== null && (
                      <div className="h-full transition-all" style={{ width: `${val}%`, background: t.color }} />
                    )}
                  </div>
                  <div className="text-[8px] mt-0.5 opacity-60">
                    <span className="font-bold">{t.label}</span> {val !== null ? val : "—"}
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* iter679 — Live cross-twin cascade animation (meal-logged etc.) */}
      <TwinCascadeToast />

      {/* iter681 — Multi-twin leverage scoring (top 4 ranked interventions) */}
      <div className="mb-3">
        <TwinLeverageCard />
      </div>

      {/* iter679 — Twin Mood summary line: a one-glance read of how the */}
      {/* whole system feels right now. Derived purely from sub_scores. */}
      {(() => {
        const moodSubScores = { ...(twinScore?.sub_scores || {}), bhi: bhiComposite };
        const m = summariseMoods(moodSubScores);
        const total = m.calm + m.steady + m.tense + m.alert + m.unknown;
        if (total === 0) return null;
        return (
          <div
            data-testid="twin-mood-summary"
            className={`mb-3 px-3 py-2 rounded-lg flex flex-wrap items-center gap-x-3 gap-y-1 text-[11px] ${
              isDark ? "bg-slate-800/40 border border-slate-700/40" : "bg-slate-50 border border-slate-200/60"
            }`}
          >
            <span className="opacity-60 uppercase font-bold tracking-wider">Днес</span>
            {m.calm > 0 && (
              <span className="flex items-center gap-1">
                <span className="inline-block w-2 h-2 rounded-full bg-emerald-500" />
                {m.calm} спокойни
              </span>
            )}
            {m.steady > 0 && (
              <span className="flex items-center gap-1">
                <span className="inline-block w-2 h-2 rounded-full bg-blue-500" />
                {m.steady} стабилни
              </span>
            )}
            {m.tense > 0 && (
              <span className="flex items-center gap-1">
                <span className="inline-block w-2 h-2 rounded-full bg-amber-500 animate-pulse" />
                {m.tense} напрегнати
              </span>
            )}
            {m.alert > 0 && (
              <span className="flex items-center gap-1">
                <span className="inline-block w-2 h-2 rounded-full bg-rose-500 animate-pulse" />
                {m.alert} нуждаят внимание
              </span>
            )}
            {m.unknown > 0 && (
              <span className="flex items-center gap-1 opacity-50">
                <span className="inline-block w-2 h-2 rounded-full bg-slate-400" />
                {m.unknown} без данни
              </span>
            )}
          </div>
        );
      })()}

      {/* Плочки на близнаците (iter1044 — + Кръв, Нервен, Мъжко здраве; броят е динамичен) */}
      <div className="text-[10px] opacity-60 mb-1.5" data-testid="twin-tiles-hint">
        Плочката отваря мрежата само с връзките на този близнак.
      </div>
      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-7 gap-2">
        {/* Nutritional */}
        <button
          onClick={() => openTwinNetwork("nutritional")}
          data-testid="twin-tile-nutritional"
          title={twinTitle("nutritional")}
          className={`p-3 rounded-xl text-left transition-all hover:shadow-md ${isDark ? "bg-emerald-500/10 hover:bg-emerald-500/15 border border-emerald-500/20" : "bg-emerald-50 hover:bg-emerald-100 border border-emerald-200"}`}
        >
          <div className="flex items-center gap-1 mb-1 min-w-0">
            <Apple className="w-3.5 h-3.5 text-emerald-500" />
            <span className="text-[10px] font-bold opacity-70 leading-tight break-words min-w-0">{twinShort("nutritional")}</span>
          </div>
          <div className={`text-2xl font-bold ${dilColor(nutri.dil_today)}`}>
            {nutri.dil_today != null ? `${nutri.dil_today}g` : "—"}
          </div>
          <div className="text-[10px] opacity-60">
            DIL днес · {nutri.meals_today || 0} ястия
          </div>
        </button>


        {/* Lab */}
        <button
          onClick={() => openTwinNetwork("lab")}
          data-testid="twin-tile-lab"
          title={twinTitle("lab")}
          className={`p-3 rounded-xl text-left transition-all hover:shadow-md ${isDark ? "bg-sky-500/10 hover:bg-sky-500/15 border border-sky-500/20" : "bg-sky-50 hover:bg-sky-100 border border-sky-200"}`}
        >
          <div className="flex items-center gap-1 mb-1 min-w-0">
            <FlaskConical className="w-3.5 h-3.5 text-sky-500" />
            <span className="text-[10px] font-bold opacity-70 leading-tight break-words min-w-0">{twinShort("lab")}</span>
          </div>
          <div className="text-2xl font-bold text-sky-500">
            {labCount > 0 ? labCount : "—"}
          </div>
          <div className="text-[10px] opacity-60">
            {labCount > 0 ? "показателя" : "няма данни"}
            {labValues.hba1c_pct != null && ` · HbA1c ${labValues.hba1c_pct}%`}
          </div>
        </button>

        {/* iter609 — Phenotype */}
        <button
          onClick={() => openTwinNetwork("phenotype")}
          data-testid="twin-tile-phenotype"
          title={twinTitle("phenotype")}
          className={`p-3 rounded-xl text-left transition-all hover:shadow-md ${isDark ? "bg-fuchsia-500/10 hover:bg-fuchsia-500/15 border border-fuchsia-500/20" : "bg-fuchsia-50 hover:bg-fuchsia-100 border border-fuchsia-200"}`}
        >
          <div className="flex items-center gap-1 mb-1 min-w-0">
            <span className="text-[12px]">🧬</span>
            <span className="text-[10px] font-bold opacity-70 leading-tight break-words min-w-0">{twinShort("phenotype")}</span>
          </div>
          <div className="text-2xl">
            {phenotype?.icon || "—"}
          </div>
          <div className="text-[10px] opacity-60 line-clamp-1">
            {phenotype ? phenotype.label_bg : "не е открит"}
          </div>
        </button>

        {/* iter609 — Microbiome */}
        <button
          onClick={() => openTwinNetwork("microbiome")}
          data-testid="twin-tile-microbiome"
          title={twinTitle("microbiome")}
          className={`p-3 rounded-xl text-left transition-all hover:shadow-md ${isDark ? "bg-emerald-500/10 hover:bg-emerald-500/15 border border-emerald-500/20" : "bg-emerald-50 hover:bg-emerald-100 border border-emerald-200"}`}
        >
          <div className="flex items-center gap-1 mb-1 min-w-0">
            <span className="text-[12px]">🦠</span>
            <span className="text-[10px] font-bold opacity-70 leading-tight break-words min-w-0">{twinShort("microbiome")}</span>
          </div>
          <div className={`text-2xl font-bold ${
            (microbiome?.diversity_score ?? 0) >= 70 ? "text-emerald-500"
            : (microbiome?.diversity_score ?? 0) >= 40 ? "text-amber-500"
            : "text-rose-500"
          }`}>
            {microbiome?.diversity_score ?? "—"}
          </div>
          <div className="text-[10px] opacity-60">
            {microbiome ? `${microbiome.plant_count || 0}/30 растения` : "няма данни"}
          </div>
        </button>

        {/* iter612 — Body Composition */}
        <button
          onClick={() => openTwinNetwork("body_comp")}
          data-testid="twin-tile-body-comp"
          title={twinTitle("body_comp")}
          className={`p-3 rounded-xl text-left transition-all hover:shadow-md ${isDark ? "bg-amber-500/10 hover:bg-amber-500/15 border border-amber-500/20" : "bg-amber-50 hover:bg-amber-100 border border-amber-200"}`}
        >
          <div className="flex items-center gap-1 mb-1 min-w-0">
            <span className="text-[12px]">💪</span>
            <span className="text-[10px] font-bold opacity-70 leading-tight break-words min-w-0">{twinShort("body_comp")}</span>
          </div>
          <div className="text-2xl font-bold text-amber-500">
            {bodyComp?.body_fat_pct != null ? `${bodyComp.body_fat_pct}%` : "—"}
          </div>
          <div className="text-[10px] opacity-60 line-clamp-1">
            {bodyComp ? (bodyComp.bf_band || "няма данни") : "няма данни"}
          </div>
        </button>

        {/* iter612 — Sleep & Recovery */}
        <button
          onClick={() => openTwinNetwork("sleep")}
          data-testid="twin-tile-sleep"
          title={twinTitle("sleep")}
          className={`p-3 rounded-xl text-left transition-all hover:shadow-md ${isDark ? "bg-indigo-500/10 hover:bg-indigo-500/15 border border-indigo-500/20" : "bg-indigo-50 hover:bg-indigo-100 border border-indigo-200"}`}
        >
          <div className="flex items-center gap-1 mb-1 min-w-0">
            <span className="text-[12px]">😴</span>
            <span className="text-[10px] font-bold opacity-70 leading-tight break-words min-w-0">{twinShort("sleep")}</span>
          </div>
          <div className={`text-2xl font-bold ${
            (sleep?.current?.recovery_score ?? 0) >= 75 ? "text-emerald-500"
            : (sleep?.current?.recovery_score ?? 0) >= 50 ? "text-amber-500"
            : "text-rose-500"
          }`}>
            {sleep?.current?.recovery_score ?? "—"}
          </div>
          <div className="text-[10px] opacity-60">
            {sleep?.current ? `${sleep.current.duration_hours || "?"}h · ${sleep.current.quality_1_10 || "?"}/10` : "няма данни"}
          </div>
        </button>

        {/* iter618 — Stress & Cortisol */}
        <button
          onClick={() => openTwinNetwork("stress")}
          data-testid="twin-tile-stress"
          title={twinTitle("stress")}
          className={`p-3 rounded-xl text-left transition-all hover:shadow-md ${isDark ? "bg-orange-500/10 hover:bg-orange-500/15 border border-orange-500/20" : "bg-orange-50 hover:bg-orange-100 border border-orange-200"}`}
        >
          <div className="flex items-center gap-1 mb-1 min-w-0">
            <span className="text-[12px]">⚡</span>
            <span className="text-[10px] font-bold opacity-70 leading-tight break-words min-w-0">{twinShort("stress")}</span>
          </div>
          <div className={`text-2xl font-bold ${
            (stress?.composite ?? 100) < 30 ? "text-emerald-500"
            : (stress?.composite ?? 100) < 55 ? "text-amber-500"
            : (stress?.composite ?? 100) < 75 ? "text-orange-500"
            : "text-rose-500"
          }`}>
            {stress?.composite ?? "—"}
          </div>
          <div className="text-[10px] opacity-60 line-clamp-1">
            {stress?.band || "няма данни"}
          </div>
        </button>


        {/* iter628 — Cardiovascular Twin (10) */}
        <button
          onClick={() => openTwinNetwork("cardio")}
          data-testid="twin-tile-cardio"
          title={twinTitle("cardio")}
          className={`p-3 rounded-xl text-left transition-all hover:shadow-md ${isDark ? "bg-red-500/10 hover:bg-red-500/15 border border-red-500/20" : "bg-red-50 hover:bg-red-100 border border-red-200"}`}
        >
          <div className="flex items-center gap-1 mb-1 min-w-0">
            <span className="text-[12px]">🫀</span>
            <span className="text-[10px] font-bold opacity-70 leading-tight break-words min-w-0">{twinShort("cardio")}</span>
          </div>
          <div className={`text-2xl font-bold ${
            (twinScore?.sub_scores?.cardio ?? 0) >= 80 ? "text-emerald-500"
            : (twinScore?.sub_scores?.cardio ?? 0) >= 60 ? "text-amber-500"
            : (twinScore?.sub_scores?.cardio ?? 0) > 0 ? "text-rose-500"
            : "text-slate-400"
          }`}>
            {twinScore?.sub_scores?.cardio ?? "—"}
          </div>
          <div className="text-[10px] opacity-60 line-clamp-1">
            BP · HRV · ApoB
          </div>
        </button>

        {/* iter628 — Hormonal-Reproductive Twin (11) */}
        <button
          onClick={() => openTwinNetwork("hormonal")}
          data-testid="twin-tile-hormonal"
          title={twinTitle("hormonal")}
          className={`p-3 rounded-xl text-left transition-all hover:shadow-md ${isDark ? "bg-pink-500/10 hover:bg-pink-500/15 border border-pink-500/20" : "bg-pink-50 hover:bg-pink-100 border border-pink-200"}`}
        >
          <div className="flex items-center gap-1 mb-1 min-w-0">
            <span className="text-[12px]">🌸</span>
            <span className="text-[10px] font-bold opacity-70 leading-tight break-words min-w-0">{twinShort("hormonal")}</span>
          </div>
          <div className={`text-2xl font-bold ${
            (twinScore?.sub_scores?.hormonal ?? 0) >= 80 ? "text-emerald-500"
            : (twinScore?.sub_scores?.hormonal ?? 0) >= 60 ? "text-amber-500"
            : (twinScore?.sub_scores?.hormonal ?? 0) > 0 ? "text-rose-500"
            : "text-slate-400"
          }`}>
            {twinScore?.sub_scores?.hormonal ?? "—"}
          </div>
          <div className="text-[10px] opacity-60 line-clamp-1">
            Цикъл · симптоми
          </div>
        </button>

        {/* iter628 — Metabolic-Insulin Twin (12) */}
        <button
          onClick={() => openTwinNetwork("metabolic_insulin")}
          data-testid="twin-tile-metabolic"
          title={twinTitle("metabolic_insulin")}
          className={`p-3 rounded-xl text-left transition-all hover:shadow-md ${isDark ? "bg-lime-500/10 hover:bg-lime-500/15 border border-lime-500/20" : "bg-lime-50 hover:bg-lime-100 border border-lime-200"}`}
        >
          <div className="flex items-center gap-1 mb-1 min-w-0">
            <span className="text-[12px]">💉</span>
            <span className="text-[10px] font-bold opacity-70 leading-tight break-words min-w-0">{twinShort("metabolic_insulin")}</span>
          </div>
          <div className={`text-2xl font-bold ${
            (twinScore?.sub_scores?.metabolic_insulin ?? 0) >= 80 ? "text-emerald-500"
            : (twinScore?.sub_scores?.metabolic_insulin ?? 0) >= 60 ? "text-amber-500"
            : (twinScore?.sub_scores?.metabolic_insulin ?? 0) > 0 ? "text-rose-500"
            : "text-slate-400"
          }`}>
            {twinScore?.sub_scores?.metabolic_insulin ?? "—"}
          </div>
          <div className="text-[10px] opacity-60 line-clamp-1">
            HOMA-IR · ApoB
          </div>
        </button>

        {/* iter629 — Inflammation Twin (13) */}
        <button
          onClick={() => openTwinNetwork("inflammation")}
          data-testid="twin-tile-inflammation"
          title={twinTitle("inflammation")}
          className={`p-3 rounded-xl text-left transition-all hover:shadow-md ${isDark ? "bg-orange-500/10 hover:bg-orange-500/15 border border-orange-500/20" : "bg-orange-50 hover:bg-orange-100 border border-orange-200"}`}
        >
          <div className="flex items-center gap-1 mb-1 min-w-0">
            <span className="text-[12px]">🔥</span>
            <span className="text-[10px] font-bold opacity-70 leading-tight break-words min-w-0">{twinShort("inflammation")}</span>
          </div>
          <div className={`text-2xl font-bold ${
            (twinScore?.sub_scores?.inflammation ?? 0) >= 80 ? "text-emerald-500"
            : (twinScore?.sub_scores?.inflammation ?? 0) >= 60 ? "text-amber-500"
            : (twinScore?.sub_scores?.inflammation ?? 0) > 0 ? "text-rose-500"
            : "text-slate-400"
          }`}>
            {twinScore?.sub_scores?.inflammation ?? "—"}
          </div>
          <div className="text-[10px] opacity-60 line-clamp-1">
            hsCRP · NLR · омега-3
          </div>
        </button>

        {/* iter629 — Movement Twin (14) */}
        <button
          onClick={() => openTwinNetwork("movement")}
          data-testid="twin-tile-movement"
          title={twinTitle("movement")}
          className={`p-3 rounded-xl text-left transition-all hover:shadow-md ${isDark ? "bg-cyan-500/10 hover:bg-cyan-500/15 border border-cyan-500/20" : "bg-cyan-50 hover:bg-cyan-100 border border-cyan-200"}`}
        >
          <div className="flex items-center gap-1 mb-1 min-w-0">
            <span className="text-[12px]">🏃</span>
            <span className="text-[10px] font-bold opacity-70 leading-tight break-words min-w-0">{twinShort("movement")}</span>
          </div>
          <div className={`text-2xl font-bold ${
            (twinScore?.sub_scores?.movement ?? 0) >= 80 ? "text-emerald-500"
            : (twinScore?.sub_scores?.movement ?? 0) >= 60 ? "text-amber-500"
            : (twinScore?.sub_scores?.movement ?? 0) > 0 ? "text-rose-500"
            : "text-slate-400"
          }`}>
            {twinScore?.sub_scores?.movement ?? "—"}
          </div>
          <div className="text-[10px] opacity-60 line-clamp-1">
            Стъпки · мин · VO2
          </div>
        </button>


        {/* iter629 — Liver-Kidney Twin (16) */}
        <button
          onClick={() => openTwinNetwork("liver_kidney")}
          data-testid="twin-tile-liver-kidney"
          title={twinTitle("liver_kidney")}
          className={`p-3 rounded-xl text-left transition-all hover:shadow-md ${isDark ? "bg-blue-500/10 hover:bg-blue-500/15 border border-blue-500/20" : "bg-blue-50 hover:bg-blue-100 border border-blue-200"}`}
        >
          <div className="flex items-center gap-1 mb-1 min-w-0">
            <span className="text-[12px]">🫁</span>
            <span className="text-[10px] font-bold opacity-70 leading-tight break-words min-w-0">{twinShort("liver_kidney")}</span>
          </div>
          <div className={`text-2xl font-bold ${
            (twinScore?.sub_scores?.liver_kidney ?? 0) >= 80 ? "text-emerald-500"
            : (twinScore?.sub_scores?.liver_kidney ?? 0) >= 60 ? "text-amber-500"
            : (twinScore?.sub_scores?.liver_kidney ?? 0) > 0 ? "text-rose-500"
            : "text-slate-400"
          }`}>
            {twinScore?.sub_scores?.liver_kidney ?? "—"}
          </div>
          <div className="text-[10px] opacity-60 line-clamp-1">
            ALT · GGT · eGFR
          </div>
        </button>

        {/* KHI — Kidney Health Intelligence (CKD/dialysis module) */}
        <button
          onClick={() => openTwinNetwork("kidney")}
          data-testid="twin-tile-kidney-intelligence"
          title="Kidney Health Intelligence — CKD скор, KDIGO лимити, ренално меню, диализа"
          className={`p-3 rounded-xl text-left transition-all hover:shadow-md ${isDark ? "bg-cyan-500/10 hover:bg-cyan-500/15 border border-cyan-500/20" : "bg-cyan-50 hover:bg-cyan-100 border border-cyan-200"}`}
        >
          <div className="flex items-center gap-1 mb-1 min-w-0">
            <span className="text-[12px]">🫘</span>
            <span className="text-[10px] font-bold opacity-70 leading-tight break-words min-w-0">{twinShort("kidney")}</span>
          </div>
          <div className={`text-2xl font-bold ${
            twinScore?.sub_scores?.kidney == null ? "text-slate-400"
            : twinScore.sub_scores.kidney >= 80 ? "text-emerald-500"
            : twinScore.sub_scores.kidney >= 60 ? "text-amber-500"
            : "text-rose-500"
          }`} data-testid="twin-score-kidney-value">
            {twinScore?.sub_scores?.kidney ?? "—"}
          </div>
          <div className="text-[10px] opacity-60 line-clamp-1">
            KHI · CKD · диализа
          </div>
        </button>

        {/* iter637 — Dental Twin (17) */}
        <button
          onClick={() => openTwinNetwork("dental")}
          data-testid="twin-tile-dental"
          title={twinTitle("dental") || "Дентален близнак — устна кухина, парафункции, връзка с системно здраве"}
          className={`p-3 rounded-xl text-left transition-all hover:shadow-md ${isDark ? "bg-cyan-500/10 hover:bg-cyan-500/15 border border-cyan-500/20" : "bg-cyan-50 hover:bg-cyan-100 border border-cyan-200"}`}
        >
          <div className="flex items-center gap-1 mb-1 min-w-0">
            <span className="text-[12px]">🦷</span>
            <span className="text-[10px] font-bold opacity-70 leading-tight break-words min-w-0">{twinShort("dental")}</span>
          </div>
          <div className={`text-2xl font-bold ${
            (twinScore?.sub_scores?.dental ?? 0) >= 80 ? "text-emerald-500"
            : (twinScore?.sub_scores?.dental ?? 0) >= 60 ? "text-amber-500"
            : (twinScore?.sub_scores?.dental ?? 0) > 0 ? "text-rose-500"
            : "text-slate-400"
          }`}>
            {twinScore?.sub_scores?.dental ?? "—"}
          </div>
          <div className="text-[10px] opacity-60 line-clamp-1">
            DHI · перио · бруксизъм
          </div>
        </button>

        {/* iter675 / iter687 — Breast Health Intelligence (18) — gender-aware copy */}
        {(() => {
          const gender = (user?.profile?.personal?.gender || "").toLowerCase();
          const isMale = gender === "male" || gender === "мъж" || gender === "m";
          const tipDefault = "Breast Health Intelligence — биология на гърдата";
          const tipMale = "Превенция на мъжки рак на гърдата — < 1% от случаите, но агресивен. Семейна анамнеза (BRCA2), гинекомастия и хормонален дисбаланс са основните рискови фактори.";
          const subFemale = bhiDensityPct != null ? `BDI · density ${bhiDensityPct}%` : "BDI · density · ECM";
          const subMale = "BRCA2 · хормони · палпация";
          return (
            <button
              onClick={() => openTwinNetwork("bhi")}
              data-testid="twin-tile-bhi"
              title={isMale ? tipMale : (twinTitle("bhi") || tipDefault)}
              className={`p-3 rounded-xl text-left transition-all hover:shadow-md ${isDark ? "bg-pink-500/10 hover:bg-pink-500/15 border border-pink-500/20" : "bg-pink-50 hover:bg-pink-100 border border-pink-200"}`}
            >
              <div className="flex items-center gap-1 mb-1 min-w-0">
                <span className="text-[12px]">🎗️</span>
                <span className="text-[10px] font-bold opacity-70 leading-tight break-words min-w-0">{twinShort("bhi")}{isMale ? " (♂)" : ""}</span>
              </div>
              <div className={`text-2xl font-bold ${
                bhiComposite == null ? "text-slate-400"
                : bhiComposite >= 80 ? "text-emerald-500"
                : bhiComposite >= 60 ? "text-amber-500"
                : "text-rose-500"
              }`}>
                {bhiComposite ?? "—"}
              </div>
              <div className="text-[10px] opacity-60 line-clamp-1">
                {isMale ? subMale : subFemale}
              </div>
            </button>
          );
        })()}

        {/* iter1044 — Хематологичен (20), Нервен (21), Мъжко здраве (22):
            вече имаха модул и възел в мрежата, но липсваха тук. */}
        <button
          onClick={() => openTwinNetwork("hematology")}
          data-testid="twin-tile-hematology"
          title="Хематологична интелигентност — кислороден капацитет, желязо, B12/фолат, имунен сигнал"
          className={`p-3 rounded-xl text-left transition-all hover:shadow-md ${isDark ? "bg-red-500/10 hover:bg-red-500/15 border border-red-500/20" : "bg-red-50 hover:bg-red-100 border border-red-200"}`}
        >
          <div className="flex items-center gap-1 mb-1 min-w-0">
            <span className="text-[12px]">🩸</span>
            <span className="text-[10px] font-bold opacity-70 leading-tight break-words min-w-0">{twinShort("hematology")}</span>
          </div>
          <div className={`text-2xl font-bold ${
            twinScore?.sub_scores?.hematology == null ? "text-slate-400"
            : twinScore.sub_scores.hematology >= 80 ? "text-emerald-500"
            : twinScore.sub_scores.hematology >= 60 ? "text-amber-500"
            : "text-rose-500"
          }`} data-testid="twin-score-hematology-value">
            {twinScore?.sub_scores?.hematology ?? "—"}
          </div>
          <div className="text-[10px] opacity-60 line-clamp-1">
            Hb · ферритин · B12
          </div>
        </button>

        {/* iter1061 — дишане и кислород: имаше страница и възел в мрежата,
            но липсваше плочка тук. */}
        <button
          onClick={() => openTwinNetwork("respiratory")}
          data-testid="twin-tile-respiratory"
          title="Дишане и кислород — колко въздух влиза, колко кислород минава и колко стига до тъканта"
          className={`p-3 rounded-xl text-left transition-all hover:shadow-md ${isDark ? "bg-sky-500/10 hover:bg-sky-500/15 border border-sky-500/20" : "bg-sky-50 hover:bg-sky-100 border border-sky-200"}`}
        >
          <div className="flex items-center gap-1 mb-1 min-w-0">
            <span className="text-[12px]">🌬️</span>
            <span className="text-[10px] font-bold opacity-70 leading-tight break-words min-w-0">{twinShort("respiratory")}</span>
          </div>
          <div className={`text-2xl font-bold ${
            twinScore?.sub_scores?.respiratory == null ? "text-slate-400"
            : twinScore.sub_scores.respiratory >= 80 ? "text-emerald-500"
            : twinScore.sub_scores.respiratory >= 60 ? "text-amber-500"
            : "text-rose-500"
          }`} data-testid="twin-score-respiratory-value">
            {twinScore?.sub_scores?.respiratory ?? "—"}
          </div>
          <div className="text-[10px] opacity-60 line-clamp-1">
            честота · насищане · задух
          </div>
        </button>

        <button
          onClick={() => openTwinNetwork("neuro")}
          data-testid="twin-tile-neuro"
          title="Нервна интелигентност — невропатия, автономен баланс, равновесие и риск от падане, сетива"
          className={`p-3 rounded-xl text-left transition-all hover:shadow-md ${isDark ? "bg-violet-500/10 hover:bg-violet-500/15 border border-violet-500/20" : "bg-violet-50 hover:bg-violet-100 border border-violet-200"}`}
        >
          <div className="flex items-center gap-1 mb-1 min-w-0">
            <span className="text-[12px]">🧠</span>
            <span className="text-[10px] font-bold opacity-70 leading-tight break-words min-w-0">{twinShort("neuro")}</span>
          </div>
          <div className={`text-2xl font-bold ${
            twinScore?.sub_scores?.neuro == null ? "text-slate-400"
            : twinScore.sub_scores.neuro >= 80 ? "text-emerald-500"
            : twinScore.sub_scores.neuro >= 60 ? "text-amber-500"
            : "text-rose-500"
          }`} data-testid="twin-score-neuro-value">
            {twinScore?.sub_scores?.neuro ?? "—"}
          </div>
          <div className="text-[10px] opacity-60 line-clamp-1">
            нерви · равновесие · сетива
          </div>
        </button>

        {twinScore?.sub_scores?.male_health !== undefined && (
          <button
            onClick={() => openTwinNetwork("male_health")}
            data-testid="twin-tile-male-health"
            title="Мъжки Health Twin — тестостерон, простата, еректилна функция, метаболитна връзка"
            className={`p-3 rounded-xl text-left transition-all hover:shadow-md ${isDark ? "bg-blue-600/10 hover:bg-blue-600/15 border border-blue-600/20" : "bg-blue-50 hover:bg-blue-100 border border-blue-200"}`}
          >
            <div className="flex items-center gap-1 mb-1 min-w-0">
              <span className="text-[12px]">♂️</span>
              <span className="text-[10px] font-bold opacity-70 leading-tight break-words min-w-0">{twinShort("male_health")}</span>
            </div>
            <div className={`text-2xl font-bold ${
              twinScore?.sub_scores?.male_health == null ? "text-slate-400"
              : twinScore.sub_scores.male_health >= 80 ? "text-emerald-500"
              : twinScore.sub_scores.male_health >= 60 ? "text-amber-500"
              : "text-rose-500"
            }`} data-testid="twin-score-male-health-value">
              {twinScore?.sub_scores?.male_health ?? "—"}
            </div>
            <div className="text-[10px] opacity-60 line-clamp-1">
              тестостерон · простата
            </div>
          </button>
        )}
      </div>

      {/* iter1061 — трите обединени части: страниците им остават достъпни */}
      <div className={`mt-3 p-3 rounded-xl border ${isDark ? "bg-slate-800/40 border-slate-700" : "bg-slate-50 border-slate-200"}`}
           data-testid="twin-merged-parts">
        <div className="text-[10px] uppercase font-bold opacity-60 mb-1.5">
          Обединени части
        </div>
        <p className="text-[11px] opacity-75 leading-relaxed mb-2">
          Три раздела описваха едно и също нещо на две места, затова вече
          влизат в оценката на близнака, към който принадлежат: душевното
          състояние — в напрежението, щитовидната жлеза — в хормоните,
          мисленето и паметта — в нервната система. Страниците им остават
          отворени.
        </p>
        <div className="flex flex-wrap gap-1.5">
          {[
            ["Душевно състояние", "/dashboard/mental-emotional-twin", "в Напрежение"],
            ["Щитовидна жлеза", "/dashboard/thyroid-twin", "в Хормони"],
            ["Мислене и памет", "/dashboard/cognitive-twin", "в Нервна система"],
          ].map(([label, route, into]) => (
            <button key={route} onClick={() => navigate(route)}
                    data-testid={`twin-merged-part-${route.split("/").pop()}`}
                    className={`text-[10px] px-2 py-1 rounded-lg border ${isDark ? "border-slate-700 hover:bg-slate-700/40" : "border-slate-300 hover:bg-white"}`}>
              {label} <span className="opacity-60">· {into}</span>
            </button>
          ))}
        </div>
      </div>

      {/* iter1045 — Кои близнаци още нямат данни и с кое едно действие се включват */}
      {missingTwins.length > 0 && (
        <div
          data-testid="twin-missing-data"
          className={`mt-3 p-3 rounded-xl border ${
            isDark ? "bg-amber-500/10 border-amber-500/30" : "bg-amber-50 border-amber-200"
          }`}
        >
          <div className={`text-[10px] uppercase font-bold mb-1.5 ${isDark ? "text-amber-300" : "text-amber-700"}`}>
            🔒 Още {missingTwins.length}{" "}
            {missingTwins.length === 1 ? "близнак чака данни" : "близнака чакат данни"}
          </div>
          <div className="space-y-1.5">
            {missingTwins.map((t) => (
              <div key={t.id} className="flex items-center gap-2 flex-wrap" data-testid={`twin-missing-${t.id}`}>
                <span className="text-[11px] font-semibold min-w-[110px]">{t.label}</span>
                <button
                  onClick={() => navigate(t.action_route)}
                  data-testid={`twin-missing-cta-${t.id}`}
                  className={`text-[11px] font-semibold px-2 py-1 rounded-lg transition-colors ${
                    isDark
                      ? "bg-amber-500/20 text-amber-200 hover:bg-amber-500/30"
                      : "bg-white text-amber-800 border border-amber-300 hover:bg-amber-100"
                  }`}
                >
                  {t.action_bg} →
                </button>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Cross-twin insight */}
      <div className={`mt-3 p-3 rounded-xl ${isDark ? "bg-slate-900/40 border border-slate-800" : "bg-white/80 border border-slate-200"}`} data-testid="twin-insight">
        <div className={`text-[10px] uppercase font-bold mb-1 ${isDark ? "text-indigo-300" : "text-indigo-600"}`}>
          ✨ Какво да правя днес
        </div>
        {loading ? (
          <div className="flex items-center gap-2 text-[12px] opacity-60">
            <Loader2 className="w-3.5 h-3.5 animate-spin" /> AI анализира 3-те близнака…
          </div>
        ) : (
          <p className="text-[12px] leading-relaxed">{data?.insight_bg || "Запиши едно хранене и направи 1 cognitive тест, за да получиш персонализиран съвет."}</p>
        )}
      </div>

      {/* iter630 — Cross-Twin Physiological Knowledge Graph */}
      <CrossTwinInsightsCard />

      {/* iter1030 — Cross-Twin корелациите (60 дни) са преместени в
          „Жива физиологична мрежа" (/twins/network), заедно с
          Физиологичния мост — всичко за връзките е на едно място. */}

      {/* iter605 — Phase C: Historic trend charts (progress at a glance) */}
      {trends && (
        <div className={`mt-3 p-3 rounded-xl ${isDark ? "bg-slate-900/40 border border-slate-800" : "bg-white/80 border border-slate-200"}`} data-testid="twin-trends">
          <div className="flex items-center justify-between mb-2">
            <div className={`text-[10px] uppercase font-bold ${isDark ? "text-emerald-300" : "text-emerald-700"}`}>
              📈 Progress at a glance
            </div>
            <div className="flex gap-1" data-testid="twin-trends-range">
              {[14, 30, 90].map((d) => (
                <button
                  key={d}
                  onClick={() => setTrendDays(d)}
                  data-testid={`twin-trends-range-${d}`}
                  className={`text-[10px] px-2 py-0.5 rounded-md transition ${
                    trendDays === d
                      ? (isDark ? "bg-indigo-500/30 text-indigo-200" : "bg-indigo-100 text-indigo-700")
                      : (isDark ? "text-slate-400 hover:bg-slate-800" : "text-slate-500 hover:bg-slate-100")
                  }`}
                >
                  {d}д
                </button>
              ))}
            </div>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-2">
            <TrendTile
              label="DIL дневно"
              series={trends.dil?.series}
              deltaPct={trends.dil?.delta_pct}
              samples={trends.dil?.samples}
              color="#10b981"
              unit="g"
              lowerIsBetter
              isDark={isDark}
              testid="trend-dil"
            />
            <TrendTile
              label="Когнитивен резултат"
              series={trends.cognitive?.series}
              deltaPct={trends.cognitive?.delta_pct}
              samples={trends.cognitive?.samples}
              color="#6366f1"
              unit=""
              isDark={isDark}
              testid="trend-cognitive"
            />
            <TrendTile
              label="HbA1c"
              series={trends.lab?.hba1c_pct?.series}
              deltaPct={trends.lab?.hba1c_pct?.delta_pct}
              samples={trends.lab?.hba1c_pct?.series?.length || 0}
              color="#0ea5e9"
              unit="%"
              lowerIsBetter
              isDark={isDark}
              testid="trend-hba1c"
            />
          </div>
        </div>
      )}

      {/* CTA row */}
      <div className="mt-3 flex flex-wrap gap-2">
        <Button size="sm" variant="ghost" onClick={() => navigate("/dashboard/cognitive-twin")} className="text-indigo-500" data-testid="twin-cta-cognitive">
          🧠 Когнитивен тест <ArrowRight className="w-3 h-3 ml-1" />
        </Button>
        <Button size="sm" variant="ghost" onClick={() => navigate("/dashboard/lab-twin")} className="text-sky-500" data-testid="twin-cta-lab">
          🧪 Lab симулация <ArrowRight className="w-3 h-3 ml-1" />
        </Button>
        <Button size="sm" variant="ghost" onClick={() => navigate("/dashboard/plate-simulator")} className="text-emerald-500" data-testid="twin-cta-plate">
          🍽 Симулатор на чинията <ArrowRight className="w-3 h-3 ml-1" />
        </Button>
      </div>
    </div>
  );
}

function TrendTile({ label, series, deltaPct, samples, color, unit = "", lowerIsBetter = false, isDark, testid }) {
  const safeSeries = Array.isArray(series) ? series : [];
  const points = safeSeries
    .map((p) => (p.value != null ? { date: p.date, value: p.value } : null))
    .filter(Boolean);

  // Trend tone: lowerIsBetter inverts (e.g. DIL going down = good)
  let toneCls = "text-slate-400";
  if (deltaPct != null && Math.abs(deltaPct) >= 1.5) {
    const goingUp = deltaPct > 0;
    const isGood = lowerIsBetter ? !goingUp : goingUp;
    toneCls = isGood ? "text-emerald-500" : "text-rose-500";
  }
  const ArrowIcon = deltaPct == null
    ? Minus
    : deltaPct > 0
      ? TrendingUp
      : TrendingDown;

  const lastVal = points.length ? points[points.length - 1].value : null;
  const firstVal = points.length ? points[0].value : null;

  return (
    <div
      data-testid={testid}
      className={`p-2 rounded-md ${isDark ? "bg-slate-800/40" : "bg-slate-50"}`}
    >
      <div className="flex items-center justify-between">
        <div className="text-[9px] uppercase font-bold opacity-60 truncate">{label}</div>
        <div className={`flex items-center gap-0.5 text-[10px] font-semibold ${toneCls}`}>
          <ArrowIcon className="w-3 h-3" />
          {deltaPct != null ? `${deltaPct > 0 ? "+" : ""}${deltaPct}%` : "—"}
        </div>
      </div>
      <div className="flex items-baseline gap-1">
        <div className="text-base font-bold" style={{ color }}>
          {lastVal != null ? `${lastVal}${unit}` : "—"}
        </div>
        {firstVal != null && firstVal !== lastVal && (
          <div className="text-[9px] opacity-50">от {firstVal}{unit}</div>
        )}
      </div>
      <div className="h-12 -mx-1" data-testid={`${testid}-chart`}>
        {points.length >= 2 ? (
          <ResponsiveContainer width="100%" height="100%">
            <LineChart data={points} margin={{ top: 4, right: 4, bottom: 0, left: 4 }}>
              <XAxis dataKey="date" hide />
              <YAxis hide domain={["auto", "auto"]} />
              <RTooltip
                contentStyle={{
                  background: isDark ? "#0f172a" : "#fff",
                  border: `1px solid ${isDark ? "#334155" : "#e2e8f0"}`,
                  borderRadius: 8,
                  fontSize: 11,
                  padding: "4px 8px",
                }}
                labelStyle={{ fontSize: 10, opacity: 0.7 }}
                formatter={(v) => [`${v}${unit}`, label]}
              />
              {points.length >= 2 && (
                <ReferenceLine
                  y={points[0].value}
                  stroke={isDark ? "#475569" : "#cbd5e1"}
                  strokeDasharray="2 2"
                  strokeOpacity={0.7}
                />
              )}
              <Line
                type="monotone"
                dataKey="value"
                stroke={color}
                strokeWidth={2}
                dot={{ r: 2, fill: color, strokeWidth: 0 }}
                activeDot={{ r: 4 }}
                isAnimationActive={false}
              />
            </LineChart>
          </ResponsiveContainer>
        ) : (
          <div className="flex items-center justify-center h-full text-[10px] opacity-50">
            {samples > 0 ? "Само 1 точка — добави още" : "Няма данни в периода"}
          </div>
        )}
      </div>
    </div>
  );
}
